import { toast } from "react-toastify";
import { i18n } from "../translate/i18n";
import { isString } from 'lodash';

const MEDIA_ERROR_MAP = {
    ERR_MEDIA_IMAGE_TOO_LARGE: { toastKey: "uploads.toasts.imageTooLarge", limitMB: 16 },
    ERR_MEDIA_VIDEO_TOO_LARGE: { toastKey: "uploads.toasts.videoTooLarge", limitMB: 16 },
    ERR_MEDIA_AUDIO_TOO_LARGE: { toastKey: "uploads.toasts.audioTooLarge", limitMB: 16 },
    ERR_MEDIA_DOCUMENT_TOO_LARGE: { toastKey: "uploads.toasts.documentTooLarge", limitMB: 100 },
    ERR_FILE_TOO_LARGE: { toastKey: "uploads.toasts.genericTooLarge", limitMB: 16 },
    LIMIT_FILE_SIZE: { toastKey: "uploads.toasts.genericTooLarge", limitMB: 16 },
};

const DEFAULT_LIMIT_BY_TOAST = {
    "uploads.toasts.imageTooLarge": 16,
    "uploads.toasts.videoTooLarge": 16,
    "uploads.toasts.audioTooLarge": 16,
    "uploads.toasts.documentTooLarge": 100,
    "uploads.toasts.genericTooLarge": 16,
};

const showToast = (message, toastId) => {
    toast.error(message, {
        toastId: toastId || message,
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: "light",
    });
};

const formatMediaMessage = (toastKey, limitMB) => {
    const resolvedLimit = typeof limitMB === "number"
        ? limitMB
        : DEFAULT_LIMIT_BY_TOAST[toastKey];

    if (i18n.exists(toastKey)) {
        const params = typeof resolvedLimit === "number" ? { limit: resolvedLimit } : undefined;
        return i18n.t(toastKey, params);
    }

    if (i18n.exists("uploads.toasts.genericTooLarge")) {
        const genericLimit = DEFAULT_LIMIT_BY_TOAST["uploads.toasts.genericTooLarge"];
        return i18n.t("uploads.toasts.genericTooLarge", genericLimit ? { limit: genericLimit } : undefined);
    }

    return "Arquivo excede o limite permitido.";
};

const SUPPORT_BETA_API_ORIGIN = "https://beta-api.neozapp.com.br";

export const isSilentSupportBetaRead = err => {
    if (
        err?.response?.status !== 403 ||
        err?.response?.data?.error !== "ERR_SUPPORT_BETA_EXTERNAL_ACTION_DISABLED" ||
        String(err?.config?.method || "").toUpperCase() !== "GET"
    ) {
        return false;
    }

    try {
        const requestUrl = new URL(
            err.config?.url || "",
            err.config?.baseURL || (typeof window !== "undefined" ? window.location.origin : undefined)
        );
        return requestUrl.origin === SUPPORT_BETA_API_ORIGIN;
    } catch (_error) {
        return false;
    }
};

const resolveMediaError = (err) => {
    if (!err) return null;

    if (err.config?.neoUploadToastKey) {
        return {
            toastKey: err.config.neoUploadToastKey,
            limitMB: err.config.neoUploadLimitMB,
            toastId: err.config.neoUploadToastKey,
        };
    }

    const errorCode = err.response?.data?.error;
    if (errorCode && MEDIA_ERROR_MAP[errorCode]) {
        return {
            ...MEDIA_ERROR_MAP[errorCode],
            toastId: errorCode,
        };
    }

    if (err.response?.status === 413) {
        return {
            toastKey: "uploads.toasts.genericTooLarge",
            limitMB: err.config?.neoUploadLimitMB ?? MEDIA_ERROR_MAP.ERR_FILE_TOO_LARGE.limitMB,
            toastId: "ERR_FILE_TOO_LARGE",
        };
    }

    if (!err.response && err.config?.neoUpload) {
        return {
            toastKey: err.config.neoUploadToastKey || "uploads.toasts.genericTooLarge",
            limitMB: err.config.neoUploadLimitMB ?? MEDIA_ERROR_MAP.ERR_FILE_TOO_LARGE.limitMB,
            toastId: err.config.neoUploadToastKey || "ERR_FILE_TOO_LARGE",
        };
    }

    return null;
};

const toastError = err => {
    if (isSilentSupportBetaRead(err)) return;

    const mediaError = resolveMediaError(err);
    if (mediaError) {
        const message = formatMediaMessage(mediaError.toastKey, mediaError.limitMB);
        showToast(message, mediaError.toastId);
        return;
    }

    const errorMsg = err.response?.data?.error;
    if (errorMsg) {
        const mediaMapEntry = MEDIA_ERROR_MAP[errorMsg];
        if (mediaMapEntry) {
            const message = formatMediaMessage(mediaMapEntry.toastKey, mediaMapEntry.limitMB);
            showToast(message, errorMsg);
            return;
        }

        const translationKey = `backendErrors.${errorMsg}`;
        if (i18n.exists(translationKey)) {
            console.error(`Error: ${i18n.t(translationKey)}`);
            showToast(i18n.t(translationKey), errorMsg);
            return;
        }

        showToast(errorMsg, errorMsg);
        return;
    }

    if (isString(err)) {
        console.error(`Error: ${err}`);
        return;
    }

    console.error("An error occurred!");
};

export default toastError;
